import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Search, Plus, MapPin, BadgeCheck, CalendarDays, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import Flag from '../components/world/Flag';
import SuggestDialog from '../components/world/SuggestDialog';
import { useJazyk } from '../context/LanguageContext';
import { API } from '../lib/api';
import { najdiDruh } from '../lib/directoryKinds';
import { plural } from '../lib/utils';

/**
 * Seznam jednoho druhu záznamů v jedné zemi – „Organizace v Česku".
 *
 * Stránkuje se po serveru. Katalog má růst do desítek tisíc záznamů a natáhnout
 * je všechny do prohlížeče by dřív nebo později přestalo jít.
 *
 * Hledání i stránkování se propisují do adresy, aby se dal výsledek poslat
 * odkazem a fungovalo tlačítko zpět.
 */

const NA_STRANKU = 24;

const datum = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

export default function DirectoryListPage() {
  const { slug, kind } = useParams();
  const navigate = useNavigate();
  const [parametry, setParametry] = useSearchParams();
  const { t, dvojjazycne } = useJazyk();

  const [zeme, setZeme] = useState(null);
  const [odpoved, setOdpoved] = useState(null);
  const [nacita, setNacita] = useState(true);
  const [chyba, setChyba] = useState('');
  const [navrhOtevren, setNavrhOtevren] = useState(false);
  const [mista, setMista] = useState([]);

  const druh = najdiDruh(kind);
  const hledani = parametry.get('q') || '';
  const kategorie = parametry.get('kategorie') || '';
  const mesto = parametry.get('mesto') || '';
  const offset = Number(parametry.get('od') || 0);

  const [hledaniVstup, setHledaniVstup] = useState(hledani);

  // Adresa je zdroj pravdy, ne stav komponenty – jinak by se výsledek nedal
  // poslat odkazem a tlačítko zpět by dělalo nesmysly.
  const zmenParametr = useCallback((zmeny) => {
    const nove = new URLSearchParams(parametry);
    Object.entries(zmeny).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) nove.delete(k);
      else nove.set(k, String(v));
    });
    setParametry(nove, { replace: false });
  }, [parametry, setParametry]);

  useEffect(() => { setHledaniVstup(hledani); }, [hledani]);

  useEffect(() => {
    if (!druh) { setChyba('neznamyDruh'); setNacita(false); return; }
    let zivy = true;
    setNacita(true);
    setChyba('');
    (async () => {
      try {
        const z = await axios.get(`${API}/geo/countries/${slug}`);
        if (!zivy) return;
        setZeme(z.data);

        const { data } = await axios.get(`${API}/directory/${kind}`, {
          params: {
            country_iso2: z.data.iso2,
            search: hledani || undefined,
            category: kategorie || undefined,
            city_name: mesto || undefined,
            limit: NA_STRANKU,
            offset,
          },
        });
        if (zivy) setOdpoved(data);

        // Města se načítají zvlášť a nezávisle na filtru – kdyby se bralo
        // z výsledků, zmizely by po výběru města všechny ostatní a nedalo by
        // se přepnout jinam.
        const m = await axios.get(`${API}/directory-places/${z.data.iso2}`)
          .catch(() => ({ data: [] }));
        if (zivy) setMista(m.data);
      } catch (e) {
        if (zivy) setChyba(e?.response?.status === 404 ? 'neznamaZeme' : 'chyba');
      } finally {
        if (zivy) setNacita(false);
      }
    })();
    return () => { zivy = false; };
  }, [slug, kind, hledani, kategorie, mesto, offset, druh]);

  // Kategorie nabízíme jen ty, které se ve výsledcích opravdu objevují –
  // prázdný filtr, který nic nenajde, je horší než žádný.
  const kategorieVNabidce = useMemo(() => {
    const vsechny = new Set();
    (odpoved?.items || []).forEach((z) => (z.categories || []).forEach((c) => vsechny.add(c)));
    return [...vsechny].sort((a, b) => a.localeCompare(b, 'cs'));
  }, [odpoved]);

  const nazevZeme = dvojjazycne(zeme?.name_cs, zeme?.name_en);

  // Ve výpisu organizací nemá smysl nabízet město, kde je jen akce.
  const mistaSObsahem = useMemo(
    () => mista.filter((m) => (m.counts?.[kind] || 0) > 0).slice(0, 24),
    [mista, kind]);

  if (chyba) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-bloom-sub">
          {chyba === 'neznamaZeme' ? t('zeme.neznama') : t(`vypis.${chyba}`)}
        </p>
        <Link to="/world" className="mt-4 inline-flex items-center gap-1.5 text-bloom-violet font-medium">
          <ArrowLeft className="w-4 h-4" /> {t('zeme.mapaSveta')}
        </Link>
      </div>
    );
  }

  const celkem = odpoved?.total || 0;
  const strankaOd = offset + 1;
  const strankaDo = Math.min(offset + NA_STRANKU, celkem);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="directory-list">
      <button type="button" onClick={() => navigate(`/world/${slug}`)}
              className="inline-flex items-center gap-1.5 text-sm text-bloom-sub hover:text-bloom-violet">
        <ArrowLeft className="w-4 h-4" /> {t('detail.zpetNa', { zeme: nazevZeme })}
      </button>

      <header className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {zeme && <Flag emoji={zeme.flag} iso2={zeme.iso2} size="lg" />}
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-bloom-text">
              {t(`sekce.${kind}`)}
            </h1>
            <p className="text-bloom-sub text-sm">
              {nazevZeme}
              {celkem > 0 && ` · ${t('vypis.zaznamu', { pocet: celkem })}`}
            </p>
          </div>
        </div>
        <button type="button" onClick={() => setNavrhOtevren(true)}
                className="inline-flex items-center gap-1.5 text-sm bg-white border border-border/50 text-bloom-sub rounded-xl px-3 py-2 hover:text-bloom-violet hover:border-bloom-violet/30"
                data-testid="list-suggest">
          <Plus className="w-4 h-4" /> {t('vypis.navrhnout')}
        </button>
      </header>

      <form className="mt-5 flex flex-col sm:flex-row gap-3"
            onSubmit={(e) => { e.preventDefault(); zmenParametr({ q: hledaniVstup, od: 0 }); }}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub" />
          <Input value={hledaniVstup} onChange={(e) => setHledaniVstup(e.target.value)}
                 placeholder={t('vypis.hledatVZemi')} className="pl-9"
                 aria-label={t('obecne.hledat')} data-testid="list-search" />
        </div>
        {kategorieVNabidce.length > 0 && (
          <select value={kategorie} onChange={(e) => zmenParametr({ kategorie: e.target.value, od: 0 })}
                  aria-label={t('vypis.kategorie')} data-testid="list-category"
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm text-bloom-text">
            <option value="">{t('vypis.vsechnyKategorie')}</option>
            {kategorieVNabidce.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        )}
      </form>

      {/* Města, ve kterých v téhle zemi opravdu něco je. Nabídnout město,
          kde nic není, je horší než nenabídnout nic. */}
      {mistaSObsahem.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="list-mesta">
          <button type="button" onClick={() => zmenParametr({ mesto: '', od: 0 })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    mesto ? 'bg-white text-bloom-sub border-border/50 hover:border-bloom-violet/40'
                          : 'bg-bloom-violet text-white border-bloom-violet'}`}>
            {t('vypis.celaZeme')}
          </button>
          {mistaSObsahem.map((m) => (
            <button key={m.city_name} type="button"
                    onClick={() => zmenParametr({ mesto: m.city_name, od: 0 })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors inline-flex items-center gap-1.5 ${
                      mesto.toLowerCase() === m.city_name.toLowerCase()
                        ? 'bg-bloom-violet text-white border-bloom-violet'
                        : 'bg-white text-bloom-sub border-border/50 hover:border-bloom-violet/40'}`}
                    data-testid={`list-mesto-${m.city_name}`}>
              <MapPin className="w-3 h-3" />{m.city_name}
              <span className="opacity-70">{m.counts[kind]}</span>
            </button>
          ))}
        </div>
      )}

      {nacita ? (
        <div className="py-16 flex justify-center"><div className="spinner" /></div>
      ) : celkem === 0 ? (
        <div className="py-14 text-center">
          <p className="text-bloom-sub">
            {hledani || kategorie || mesto
              ? t('vypis.nicNeodpovida')
              : t('vypis.zadneZaznamy', { zeme: nazevZeme })}
          </p>
          <button type="button" onClick={() => setNavrhOtevren(true)}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white bg-bloom-violet hover:bg-bloom-violet/90 px-5 py-2.5 rounded-xl">
            <Plus className="w-4 h-4" /> Navrhnout {druh?.jednotne}
          </button>
        </div>
      ) : (
        <>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="list-items">
            {odpoved.items.map((z) => (
              <li key={z.id}>
                <Link to={`/world/${slug}/${kind}/${z.slug || z.id}`}
                      className="block h-full bg-white border border-border/40 rounded-2xl p-4 hover:border-bloom-violet/40 hover:shadow-sm transition-all">
                  <div className="flex items-start gap-2">
                    <p className="font-heading font-bold text-bloom-text flex-1">{z.name}</p>
                    {z.verification === 'verified' && (
                      <BadgeCheck className="w-4 h-4 text-bloom-mint shrink-0" />
                    )}
                  </div>
                  {z.description && (
                    <p className="mt-1.5 text-sm text-bloom-sub leading-relaxed line-clamp-3">
                      {z.description}
                    </p>
                  )}
                  {z.details?.starts_at && (
                    <p className="mt-2 text-xs text-bloom-violet flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" />{datum(z.details.starts_at)}
                    </p>
                  )}
                  {z.location?.city_name && (
                    <p className="mt-1.5 text-xs text-bloom-sub flex items-center gap-1">
                      <MapPin className="w-3 h-3" />{z.location.city_name}
                    </p>
                  )}
                </Link>
              </li>
            ))}
          </ul>

          {celkem > NA_STRANKU && (
            <nav className="mt-7 flex items-center justify-between gap-3" aria-label={t('obecne.strankovani')}>
              <button type="button" disabled={offset === 0}
                      onClick={() => zmenParametr({ od: Math.max(0, offset - NA_STRANKU) })}
                      className="inline-flex items-center gap-1 text-sm text-bloom-violet disabled:text-bloom-sub/40 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" /> {t('vypis.predchozi')}
              </button>
              <span className="text-sm text-bloom-sub">{strankaOd}–{strankaDo} z {celkem}</span>
              <button type="button" disabled={strankaDo >= celkem}
                      onClick={() => zmenParametr({ od: offset + NA_STRANKU })}
                      className="inline-flex items-center gap-1 text-sm text-bloom-violet disabled:text-bloom-sub/40 disabled:cursor-not-allowed">
                {t('vypis.dalsi')} <ChevronRight className="w-4 h-4" />
              </button>
            </nav>
          )}
        </>
      )}

      {zeme && (
        <SuggestDialog open={navrhOtevren} onOpenChange={setNavrhOtevren}
                       countryIso2={zeme.iso2} countryName={nazevZeme}
                       vychoziDruh={kind} />
      )}
    </div>
  );
}
